import React, { useState } from 'react'; // ✅ Doğru kullanım
import { useNavigate } from 'react-router-dom';
import { Plus, Search, Edit2, Trash2, MapPin, Phone, FileText, Users, Network, Monitor, Scale, Archive, Server, HardDrive, Smartphone } from 'lucide-react';
import { apiService } from '../services/api';
import { Location } from '../types';
import RoleBasedComponent from './RoleBasedComponent';
import { useAuth } from '../contexts/AuthContext';
import { showGlobalNotification } from '../hooks/useNotifications';
import { useGlobalSearch } from '../hooks/useGlobalSearch';
import Modal from './Modal';
import { useLanguage } from '../contexts/LanguageContext';

const LocationsPage: React.FC = () => {
  const { hasPermission, user } = useAuth();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  useGlobalSearch('locations', setSearchTerm);
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState<Location | null>(null);
  const [locationsData, setLocationsData] = useState<Location[]>([]);
  const [loading, setLoading] = useState(true);
  const [locationData, setLocationData] = useState({
    name: '',
    address: '',
    phone: ''
  });

  // Load data on component mount
  React.useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const locationsResponse = await apiService.getLocations();

      // API response format kontrolü
      if (locationsResponse && Array.isArray(locationsResponse.data)) {
        setLocationsData(locationsResponse.data);
      } else if (Array.isArray(locationsResponse)) {
        setLocationsData(locationsResponse);
      } else {
        console.error('Unexpected locations response format:', locationsResponse);
        setLocationsData([]);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('locations.errLoad'));
    } finally {
      setLoading(false);
    }
  };

  const filteredLocations = Array.isArray(locationsData)
    ? locationsData.filter(location =>
        String(location?.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
        String(location?.address || '').toLowerCase().includes(searchTerm.toLowerCase())
      )
    : [];

  const handleEdit = (location: Location) => {
    setEditingLocation(location);
    setLocationData({
      name: location.name,
      address: location.address,
      phone: location.phone
    });
    setShowModal(true);
  };

  const handleAdd = () => {
    setEditingLocation(null);
    setLocationData({
      name: '',
      address: '',
      phone: ''
    });
    setShowModal(true);
  };

  const handleSave = async () => {
    if (!locationData.name || !locationData.address || !locationData.phone) {
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', t('locations.errRequired'));
      return;
    }

    try {
      if (editingLocation) {
        // Update existing location
        await apiService.updateLocation(editingLocation.id, locationData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('locations.successUpdated'));
      } else {
        // Add new location
        await apiService.createLocation(locationData);
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('locations.successCreated'));
      }

      // Reload data
      await loadData();
      setShowModal(false);
      setLocationData({ name: '', address: '', phone: '' });
    } catch (error: any) {
      console.error('Error saving location:', error);
      showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('locations.errSave'));
    }
  };

  const handleDelete = async (id: number) => {
    const locationToDelete = locationsData.find(l => l.id === id);
    if (!locationToDelete) return;

    if (confirm(t('locations.confirmDelete'))) {
      try {
        await apiService.deleteLocation(id);
        await loadData();
        showGlobalNotification.success(language === 'en' ? 'Success' : 'Başarılı', t('locations.successDeleted'));
      } catch (error: any) {
        console.error('Error deleting location:', error);
        showGlobalNotification.error(language === 'en' ? 'Error' : 'Hata', error.message || t('locations.errDelete'));
      }
    }
  };

  if (loading) {
    return (
      <div className="p-6">
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 dark:text-gray-100 mb-2">{t('locations.title')}</h1>
          <p className="text-gray-600 dark:text-gray-400">{t('locations.subtitle')}</p>
        </div>
        <RoleBasedComponent requiredPermissions={['locations.create']}>
          <button
            onClick={handleAdd}
            className="flex items-center px-4 py-2 bg-primary text-white rounded-lg hover:bg-primary-dark transition-colors mt-4 sm:mt-0"
          >
            <Plus className="w-4 h-4 mr-2" />
            {t('locations.newLocation')}
          </button>
        </RoleBasedComponent>
      </div>

      {/* Search */}
      <div className="relative mb-6">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
        <input
          type="text"
          placeholder={t('locations.searchPlaceholder')}
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent w-full sm:w-96"
        />
      </div>

      {/* Locations Table */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-700">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colLocation')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colAddress')}
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colPhone')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colUser')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colSwitch')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colIot')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colScale')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colCabinet')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colPhysicalServer')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colVirtualServer')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colGsm')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colMobile')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colCreated')}
                </th>
                <th className="px-6 py-3 text-center text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  {t('locations.colActions')}
                </th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {filteredLocations.map((location) => (
                <tr key={location.id} className="hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="w-8 h-8 bg-primary/20 rounded-lg flex items-center justify-center mr-3">
                        <MapPin className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-900 dark:text-gray-100">{location.name}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4">
                    <div className="text-sm text-gray-900 dark:text-gray-100 max-w-xs truncate" title={location.address}>
                      {location.address}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900 dark:text-gray-100">{location.phone}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/users?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-primary/20 text-primary-dark hover:bg-primary/30 transition-colors"
                        title={t('locations.colUser')}
                      >
                        <Users className="w-3 h-3 mr-1" />
                        {location.statistics.users}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/network?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-green-100 text-green-800 hover:bg-green-200 transition-colors"
                        title={t('locations.colSwitch')}
                      >
                        <Network className="w-3 h-3 mr-1" />
                        {location.statistics.switches}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/iot?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-purple-100 text-purple-800 hover:bg-purple-200 transition-colors"
                        title={t('locations.colIot')}
                      >
                        <Monitor className="w-3 h-3 mr-1" />
                        {location.statistics.iot_devices}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/scales?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-orange-100 text-orange-800 hover:bg-orange-200 transition-colors"
                        title={t('locations.colScale')}
                      >
                        <Scale className="w-3 h-3 mr-1" />
                        {location.statistics.smart_scales}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/cabinets?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-200 hover:bg-gray-200 dark:hover:bg-gray-600 dark:bg-gray-600 transition-colors"
                        title={t('locations.colCabinet')}
                      >
                        <Archive className="w-3 h-3 mr-1" />
                        {location.statistics.cabinets}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/servers/physical?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-red-100 text-red-800 hover:bg-red-200 transition-colors"
                        title={t('locations.colPhysicalServer')}
                      >
                        <Server className="w-3 h-3 mr-1" />
                        {location.statistics.physical_servers}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/servers/virtual?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800 hover:bg-indigo-200 transition-colors"
                        title={t('locations.colVirtualServer')}
                      >
                        <HardDrive className="w-3 h-3 mr-1" />
                        {location.statistics.virtual_servers}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/gsm-lines?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800 hover:bg-yellow-200 transition-colors"
                        title={t('locations.colGsm')}
                      >
                        <Smartphone className="w-3 h-3 mr-1" />
                        {location.statistics.gsm_lines}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    {location.statistics && (
                      <button
                        onClick={() => navigate(`/mobile-phones?location=${location.id}`)}
                        className="inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-teal-100 text-teal-800 hover:bg-teal-200 transition-colors"
                        title={t('locations.colMobile')}
                      >
                        <Phone className="w-3 h-3 mr-1" />
                        {location.statistics.mobile_phones}
                      </button>
                    )}
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {new Date(location.created_at).toLocaleDateString(language === 'en' ? 'en-US' : 'tr-TR')}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-center">
                    <div className="flex items-center justify-center space-x-2">
                      <RoleBasedComponent requiredPermissions={['locations.update']}>
                        <button
                          onClick={() => handleEdit(location)}
                          className="p-1 text-gray-400 hover:text-primary transition-colors"
                        >
                          <Edit2 className="w-4 h-4" />
                        </button>
                      </RoleBasedComponent>
                      <RoleBasedComponent requiredPermissions={['locations.delete']}>
                        <button
                          onClick={() => handleDelete(location.id)}
                          className="p-1 text-gray-400 hover:text-red-600 transition-colors"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </RoleBasedComponent>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {filteredLocations.length === 0 && (
          <div className="text-center py-12">
            <MapPin className="mx-auto h-12 w-12 text-gray-400" />
            <h3 className="mt-2 text-sm font-medium text-gray-900 dark:text-gray-100">{t('locations.notFound')}</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {searchTerm ? t('locations.notFoundSearch') : t('locations.notFoundEmpty')}
            </p>
          </div>
        )}
      </div>

      {/* Modal */}
      <Modal isOpen={showModal} onClose={() => setShowModal(false)}>
          <div className="bg-white dark:bg-gray-800 rounded-lg w-full max-w-md max-h-[85vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
                {editingLocation ? t('locations.editTitle') : t('locations.newTitle')}
              </h2>
            </div>

            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('locations.labelName')}
                </label>
                <input
                  type="text"
                  value={locationData.name}
                  onChange={(e) => setLocationData({...locationData, name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Örn: İstanbul Merkez"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('locations.labelAddress')}
                </label>
                <textarea
                  value={locationData.address}
                  onChange={(e) => setLocationData({...locationData, address: e.target.value})}
                  rows={3}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="Tam adres bilgisi"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  {t('locations.labelPhone')}
                </label>
                <input
                  type="text"
                  value={locationData.phone}
                  onChange={(e) => setLocationData({...locationData, phone: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                  placeholder="+90 212 XXX XX XX"
                />
              </div>
            </div>

            <div className="p-6 border-t border-gray-200 dark:border-gray-700 flex justify-end space-x-3">
              <button
                onClick={() => setShowModal(false)}
                className="px-4 py-2 text-gray-700 dark:text-gray-300 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 dark:bg-gray-700 transition-colors"
              >
                {t('locations.cancel')}
              </button>
              <button
                onClick={handleSave}
                className="px-4 py-2 bg-primary text-white rounded-md hover:bg-primary-dark transition-colors"
              >
                {editingLocation ? t('locations.update') : t('locations.save')}
              </button>
            </div>
          </div>
        </Modal>
    </div>
  );
};

export default LocationsPage;
